/* Throughout the app's reducer, we adopt a normalisation approach that consists
 * of dividing the store in two keys:
 * - byId: a multidimensional object with each item's ID (or selected property)
 *   as the key
 * - allIds: an (optional) array used to maintain the original order of the items
 * For reference, see: https://redux.js.org/recipes/structuring-reducers/normalizing-state-shape
 */
import { keyBy, uniq } from 'lodash';

/**
 * getAllIds
 * Utility to return an array or Immutable list of ids from a server's response.
 * It can also accept an array of IDs as responseArray to be merged into the
 * existing one.
 *
 * @param  {Array}      responseArray   the array of items to be normalised
 * @param  {Array|List} [prevAllIds=[]] the previous array of allIds
 * @param  {String}     [key='id']      the item object property to use as the id in byId
 * @return {Array}                      an array of IDs
 */
export const getAllIds = (responseArray, prevAllIds = [], key = 'id') => {
  // The new array is merged into the existing one and any duplicates are removed
  // We cast the IDs to strings because they reflect byId object keys, which
  // can only be strings
  const newAllIds = uniq(
    Array.from(responseArray, (item) => {
      // Only some types are valid IDs
      const isValidId = (i) => typeof i === 'string' || typeof i === 'number';

      if (!item) {
        return undefined;
      }

      const itemId = item[key];
      if (itemId && isValidId(itemId)) {
        return `${itemId}`;
      }
      if (isValidId(item)) {
        return `${item}`;
      }

      return undefined;
      // Remove all undefined items
    }).filter((item) => !!item)
  );

  const allIds = prevAllIds.concat(
    newAllIds.filter((id) => prevAllIds.indexOf(id) < 0)
  );

  return allIds;
};

/**
 * normaliseResponse
 * Utility to convert a server's response to a normalised object with
 * byId and allIds keys
 *
 * @param  {Array}      responseArray   the array of items to be normalised
 * @param  {Array|List} [prevAllIds=[]] the previous array of allIds
 * @param  {String}     [key='id']      the item object property to use as the id in byId
 * @return {Object}                     the object with byId and allIds keys
 */
export default (responseArray, prevAllIds = [], key = 'id') => {
  // The response array is transformed to a multidimensional object with
  // each selected item property as its key
  const byId = keyBy(responseArray, key);

  const allIds = getAllIds(responseArray, prevAllIds, key);

  return {
    byId,
    allIds
  };
};
